import React, { useState, useEffect } from 'react';
import { Layout } from "@/components/Layout/Layout";
import { MainHeading } from "@/components/common/MainHeading";
import { SectionCard } from "@/components/common/SectionCard";
import CommonTable, { TableColumn, Action } from "@/components/common/CommonTable";
import { TableFilters } from "@/components/common/TableFilters";
import { TableItemIdentity } from "@/components/common/TableItemIdentity";
import { StatusBadge } from "@/components/common/StatusBadge";
import Button from "@/components/common/Button";
import {
    DollarSign,
    ArrowUpRight,
    Plus,
    Search,
    Eye
} from "lucide-react";
import { formatRand, showToastNotification, GetFilterUrl } from "@/utility/helper";
import { callApi, ThrowError } from "@/api/callApi";
import { API_URL, API_ENDPOINT } from "@/api/constant";
import { Ipayout } from '@/interfaces/Payouts/payout.interface';
import { IStore } from '@/interfaces/Store/store.interface';
import { useRoleNavigate } from '@/hooks/useRoleNavigate';
import { RoutesLink } from '@/utility/RouteLinks';
import { MultiSelect } from "@/components/common/MultiSelect";
import { useFilter } from "@/hooks/useFilter";
import Pagination from "@/components/common/Pagination";
import { IStorePayoutListResponse } from '@/interfaces/IStoreSettlement/IStoreSettlement.interface';
import { PayoutDetailsModal } from "@/components/common/Model/PayoutDetailsModal";
import { usePermissions } from "@/hooks/usePermissions";
import { RESOURCES } from "@/context/permissions";

export const StoreSettlements = () => {
    const navigate = useRoleNavigate();
    const [payouts, setPayouts] = useState<Ipayout[]>([]);
    const [fetchingPayouts, setFetchingPayouts] = useState(false);
    const [processingPayoutId, setProcessingPayoutId] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedPayout, setSelectedPayout] = useState<Ipayout | null>(null);
    const { canCreate } = usePermissions();

    const [stores, setStores] = useState<IStore[]>([]);

    const { filters, debouncedFilters, updateFilter, handlePageChange, pageInfo, setPageInfo } = useFilter({
        page: 1,
        limit: 10,
        search: '',
        storeId: '',
        status: canCreate(RESOURCES.STORE_SETTLEMENT) ? '' : "approved",
    });

    const fetchStores = async () => {
        try {
            const res = await callApi<{ data: { stores: IStore[] } }>(
                `${API_URL}${API_ENDPOINT.stores}/all?page=1&limit=100`,
                { method: 'GET' }
            );
            setStores(res?.data?.stores || []);
        } catch (error) {
            ThrowError(error);
        }
    };

    useEffect(() => {
        fetchStores();
    }, []);

    // Fetch Payouts List
    const fetchPayouts = async () => {
        try {
            setFetchingPayouts(true);
            const queryParams = GetFilterUrl(debouncedFilters);
            const res = await callApi<IStorePayoutListResponse>(
                `${API_URL}${API_ENDPOINT.payouts}/lock/list?${queryParams}`,
                { method: 'GET' }
            );
            setPayouts(res?.data?.payouts || []);
            setPageInfo(res?.data?.pageInfo ?? { limit: 10, page: 1, total: 0, totalPages: 0 });
        } catch (error) {
            ThrowError(error);
        } finally {
            setFetchingPayouts(false);
        }
    };

    useEffect(() => {
        fetchPayouts();
    }, [debouncedFilters.page, debouncedFilters.limit, debouncedFilters.search, debouncedFilters.storeId]);

    const handleProceedToPay = async (payoutId: string) => {
        try {
            setProcessingPayoutId(payoutId);
            await new Promise(resolve => setTimeout(resolve, 1000));
            showToastNotification('success', 'Redirecting to payment gateway...');
        } catch (error) {
            ThrowError(error);
        } finally {
            setProcessingPayoutId(null);
        }
    };

    const columns: TableColumn<Ipayout>[] = [
        {
            key: "id",
            header: "Payout ID",
            render: (item) => <span className="font-bold text-foreground">{item.payoutRef}</span>
        },
        {
            key: "store",
            header: "Store",
            render: (item) => item.store ? (
                <div
                    className="cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => navigate(RoutesLink.storeDetails.replace(':id', item.store!.id))}
                >
                    <TableItemIdentity
                        name={item.store.name}
                        subtitle={item.store.contact}
                        image={item.store.media?.[0]?.url}
                    />
                </div>
            ) : (
                <span className="text-muted-foreground">---</span>
            )
        },
        {
            key: "startDate",
            header: "Start Date",
            render: (item) => <span className="text-muted-foreground">{item.startDate ? new Date(item.startDate).toLocaleDateString() : '---'}</span>
        },
        {
            key: "endDate",
            header: "End Date",
            render: (item) => <span className="text-muted-foreground">{item.endDate ? new Date(item.endDate).toLocaleDateString() : '---'}</span>
        },
        {
            key: "amount",
            header: "Amount",
            render: (item) => <span className="font-bold text-primary">{formatRand(Number(item.amount))}</span>
        },
        {
            key: "status",
            header: "Status",
            render: (item) => <StatusBadge status={item.status} />
        },
    ];

    const actions: Action<Ipayout>[] = [{
        text: 'View Details',
        icon: <Eye size={16} strokeWidth={1.5} className="cursor-pointer" />,
        onClick: (record: Ipayout) => {
            setSelectedPayout(record);
            setIsModalOpen(true);
        },
    }];

    return (
        <Layout>
            <div className="space-y-6">
                <MainHeading
                    title="Store Settlements"
                    description="Manage created payouts and proceed to payment."
                    actionLabel={canCreate(RESOURCES.STORE_SETTLEMENT) ? "Create Payout" : undefined}
                    actionIcon={canCreate(RESOURCES.STORE_SETTLEMENT) ? <Plus className="w-4 h-4" /> : undefined}
                    onAction={() => navigate(RoutesLink.CreateStoreSettlement)}
                />

                <TableFilters
                    placeholder="Search Payout Reference..."
                    searchTerm={filters.search}
                    onSearchChange={(query) => updateFilter({ search: query, page: 1 })}
                >
                    {canCreate(RESOURCES.STORE_SETTLEMENT) && <div className="flex gap-4 flex-wrap">
                        <div className="w-[250px]">
                            <MultiSelect
                                options={stores.map(s => ({ label: s.name, value: s.id }))}
                                selected={filters.storeId ? [filters.storeId] : []}
                                onChange={(val) => updateFilter({ storeId: val[0] || "", page: 1 })}
                                placeholder="Select Store"
                                singleSelect={true}
                            />
                        </div>
                        <div className="w-[200px]">
                            <MultiSelect
                                options={[
                                    { label: 'All Status', value: '' },
                                    { label: 'Pending', value: 'pending' },
                                    { label: 'Approved', value: 'approved' },
                                    // { label: 'Rejected', value: 'rejected' },
                                ]}
                                selected={filters.status ? [filters.status] : []}
                                onChange={(val) => updateFilter({ status: val[0] || "", page: 1 })}
                                placeholder="Filter by status"
                                singleSelect={true}
                            />
                        </div>
                    </div>}
                </TableFilters>

                <SectionCard title="Created Payouts" icon={<DollarSign className="w-5 h-5 text-primary" />}>
                    <CommonTable
                        columns={columns}
                        data={payouts}
                        loading={fetchingPayouts}
                        emptyMessage="No payouts found. Create a payout first."
                        actions={actions}
                    />
                    <Pagination
                        currentPage={filters.page || 1}
                        totalPages={pageInfo.totalPages}
                        totalItems={pageInfo.total}
                        itemsPerPage={pageInfo.limit}
                        onPageChange={handlePageChange}
                    />
                </SectionCard>
            </div>

            <PayoutDetailsModal
                open={isModalOpen}
                onOpenChange={setIsModalOpen}
                payout={selectedPayout}
                onSuccess={fetchPayouts}
                type='store'
            />
        </Layout>
    );
};
